import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { ApiService } from '../../core/services/api.service';
import { TimeTrackingService } from './time-tracking.service';
import { TimeEntryDto, TIME_ENTRY_STATUS_LABELS, TimesheetDayDto, TimesheetPreset } from './time-tracking.models';
import { PmReportsApiService } from '../pm-reports/pm-reports.service';
import { ResourceUtilizationReportDto } from '../pm-reports/pm-reports.models';

interface ProjectOption { id: string; name: string; }
interface IssueOption { id: string; key: string; summary: string; }

@Component({
  selector: 'app-time-tracking',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatCardModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule,
    MatTableModule, MatTabsModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatChipsModule, MatCheckboxModule
  ],
  styles: [
    `
      .tt-page { padding: 1.25rem; display: grid; gap: 1rem; max-width: 1100px; }
      .subtitle { opacity: 0.75; margin: 0.25rem 0 0; }
      .toolbar { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
      .error-banner { background: #fdecea; color: #b71c1c; padding: 0.75rem 1rem; border-radius: 8px; }
      .running { color: #1565c0; font-weight: 600; }
      .hint { opacity: 0.7; font-size: 0.9rem; }
      table { width: 100%; }
      .tab-body { padding: 1rem 0; }
      .form-row { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-start; margin-bottom: 1rem; }
      .grow { flex: 1; min-width: 180px; }
      .timesheet-day { margin-bottom: 1rem; }
      .timesheet-day h4 { margin: 0 0 0.5rem; }
      .entry-actions { display: flex; gap: 0.35rem; flex-wrap: wrap; }
      .totals { display: flex; flex-wrap: wrap; gap: 0.75rem; margin: 0.5rem 0 1rem; }
      .total-chip { font-size: 0.85rem; padding: 0.35rem 0.75rem; border-radius: 999px; background: #f1f5f9; }
      .total-chip.billable { background: #ecfdf5; color: #047857; }
      .total-chip.non-billable { background: #fff7ed; color: #c2410c; }
      .util-section { margin-top: 1.5rem; }
      mat-chip.running { background: #e3f2fd; color: #1565c0; }
      mat-chip.draft { background: #f5f5f5; }
      mat-chip.submitted { background: #fff8e1; color: #f57f17; }
      mat-chip.approved { background: #e8f5e9; color: #2e7d32; }
      mat-chip.rejected { background: #ffebee; color: #c62828; }
      button.active { border-color: #1565c0; color: #1565c0; }
    `
  ],
  template: `
<div class="tt-page">
  <div>
    <h1>Time tracking</h1>
    <p class="subtitle">Manage timers, work logs, and timesheets.</p>
  </div>

  @if (error()) {
    <div class="error-banner">{{ error() }}</div>
  }

  <mat-tab-group>
    <mat-tab label="My Timers">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Project</mat-label>
            <mat-select [(ngModel)]="timerProjectId" (ngModelChange)="loadProjectIssues($event)">
              @for (p of projects(); track p.id) {
                <mat-option [value]="p.id">{{ p.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Issue (optional)</mat-label>
            <mat-select [(ngModel)]="timerIssueId">
              <mat-option value="">None</mat-option>
              @for (i of projectIssues(); track i.id) {
                <mat-option [value]="i.id">{{ i.key }} — {{ i.summary }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Description</mat-label>
            <input matInput [(ngModel)]="timerDescription" />
          </mat-form-field>
          <button mat-flat-button color="primary" type="button" (click)="startTimer()">
            <mat-icon>play_arrow</mat-icon> Start
          </button>
        </div>

        <mat-card>
          <table mat-table [dataSource]="runningEntries()" class="mat-elevation-z0">
            <ng-container matColumnDef="description">
              <th mat-header-cell *matHeaderCellDef>Description</th>
              <td mat-cell *matCellDef="let row">{{ row.description || '—' }}</td>
            </ng-container>
            <ng-container matColumnDef="hours">
              <th mat-header-cell *matHeaderCellDef>Hours</th>
              <td mat-cell *matCellDef="let row"><span class="running">{{ liveHours(row) }}h</span></td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                <div class="entry-actions">
                  <button mat-stroked-button type="button" (click)="pauseTimer(row)">Pause</button>
                  <button mat-stroked-button type="button" (click)="stopTimer(row)">Stop</button>
                </div>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="timerColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: timerColumns"></tr>
          </table>
          @if (!runningEntries().length) {
            <p class="hint" style="padding: 1rem">No running timers.</p>
          }
        </mat-card>
      </div>
    </mat-tab>

    <mat-tab label="Work Log">
      <div class="tab-body">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Project</mat-label>
            <mat-select [(ngModel)]="manualProjectId" (ngModelChange)="onManualProjectChange($event)">
              @for (p of projects(); track p.id) {
                <mat-option [value]="p.id">{{ p.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Issue (optional)</mat-label>
            <mat-select [(ngModel)]="logIssueId">
              <mat-option value="">None</mat-option>
              @for (i of projectIssues(); track i.id) {
                <mat-option [value]="i.id">{{ i.key }} — {{ i.summary }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Date</mat-label>
            <input matInput type="date" [(ngModel)]="manualDate" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Hours</mat-label>
            <input matInput type="number" [(ngModel)]="manualHours" min="0.25" step="0.25" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Description</mat-label>
            <input matInput [(ngModel)]="manualDescription" />
          </mat-form-field>
          <mat-checkbox [(ngModel)]="manualBillable">Billable</mat-checkbox>
          <button mat-flat-button color="primary" type="button" [disabled]="!manualHours" (click)="createManual()">
            Log time
          </button>
          <button mat-stroked-button type="button" (click)="load()">
            <mat-icon>refresh</mat-icon> Refresh
          </button>
        </div>

        @if (loading()) { <mat-spinner diameter="28"></mat-spinner> }

        <mat-card>
          <table mat-table [dataSource]="rows()" class="mat-elevation-z0">
            <ng-container matColumnDef="date">
              <th mat-header-cell *matHeaderCellDef>Date</th>
              <td mat-cell *matCellDef="let row">{{ row.date }}</td>
            </ng-container>
            <ng-container matColumnDef="description">
              <th mat-header-cell *matHeaderCellDef>Description</th>
              <td mat-cell *matCellDef="let row">{{ row.description || '—' }}</td>
            </ng-container>
            <ng-container matColumnDef="hours">
              <th mat-header-cell *matHeaderCellDef>Hours</th>
              <td mat-cell *matCellDef="let row">{{ row.hours }}{{ row.isBillable ? ' · billable' : '' }}</td>
            </ng-container>
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let row">
                <mat-chip [class]="statusChipClass(row)">{{ statusLabel(row.status) }}</mat-chip>
              </td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row">
                @if (row.status === 0 && !row.isRunning) {
                  <button mat-stroked-button type="button" (click)="submitEntry(row)">Submit</button>
                }
                @if (!row.isRunning && row.startedAt && !row.endedAt) {
                  <button mat-stroked-button type="button" (click)="resumeTimer(row)">Resume</button>
                }
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="logColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: logColumns"></tr>
          </table>
        </mat-card>

        <section class="util-section">
          <h3>Team utilization</h3>
          <p class="hint">Uses project resource-utilization analytics when a project is selected.</p>
          @if (loadingUtil()) { <mat-spinner diameter="24"></mat-spinner> }
          @if (utilization(); as util) {
            <table mat-table [dataSource]="util.assignees" class="mat-elevation-z0">
              <ng-container matColumnDef="employeeName">
                <th mat-header-cell *matHeaderCellDef>Employee</th>
                <td mat-cell *matCellDef="let r">{{ r.employeeName }}</td>
              </ng-container>
              <ng-container matColumnDef="openIssues">
                <th mat-header-cell *matHeaderCellDef>Open</th>
                <td mat-cell *matCellDef="let r">{{ r.openIssues }}</td>
              </ng-container>
              <ng-container matColumnDef="remainingStoryPoints">
                <th mat-header-cell *matHeaderCellDef>Remaining pts</th>
                <td mat-cell *matCellDef="let r">{{ r.remainingStoryPoints }}</td>
              </ng-container>
              <ng-container matColumnDef="availableHours">
                <th mat-header-cell *matHeaderCellDef>Available hrs</th>
                <td mat-cell *matCellDef="let r">{{ r.availableHours }}</td>
              </ng-container>
              <ng-container matColumnDef="isOverallocated">
                <th mat-header-cell *matHeaderCellDef>Overallocated</th>
                <td mat-cell *matCellDef="let r">{{ r.isOverallocated ? 'Yes' : 'No' }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="utilColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: utilColumns"></tr>
            </table>
          } @else {
            <p class="hint">Select a project to load utilization.</p>
          }
        </section>
      </div>
    </mat-tab>

    <mat-tab label="Timesheet">
      <div class="tab-body">
        <div class="toolbar">
          <button mat-stroked-button type="button" [class.active]="timesheetPreset === 'day'" (click)="setTimesheetPreset('day')">Day</button>
          <button mat-stroked-button type="button" [class.active]="timesheetPreset === 'week'" (click)="setTimesheetPreset('week')">Week</button>
          <button mat-stroked-button type="button" [class.active]="timesheetPreset === 'month'" (click)="setTimesheetPreset('month')">Month</button>
          <button mat-stroked-button type="button" (click)="loadTimesheet()">
            <mat-icon>refresh</mat-icon> Refresh timesheet
          </button>
          @if (loadingTimesheet()) { <mat-spinner diameter="24"></mat-spinner> }
        </div>
        <div class="totals">
          <span class="total-chip">Total: {{ timesheetTotal() }}h</span>
          <span class="total-chip billable">Billable: {{ timesheetBillable() }}h</span>
          <span class="total-chip non-billable">Non-billable: {{ timesheetNonBillable() }}h</span>
        </div>
        @for (day of timesheetDays(); track day.date) {
          <div class="timesheet-day">
            <h4>{{ day.date }} — {{ day.totalHours }}h</h4>
            <table mat-table [dataSource]="day.entries" class="mat-elevation-z0">
              <ng-container matColumnDef="description">
                <th mat-header-cell *matHeaderCellDef>Description</th>
                <td mat-cell *matCellDef="let row">{{ row.description || '—' }}</td>
              </ng-container>
              <ng-container matColumnDef="hours">
                <th mat-header-cell *matHeaderCellDef>Hours</th>
                <td mat-cell *matCellDef="let row">{{ row.hours }}{{ row.isBillable ? ' · billable' : '' }}</td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">{{ statusLabel(row.status) }}</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="sheetColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: sheetColumns"></tr>
            </table>
          </div>
        } @empty {
          <p class="hint">No timesheet data for this period.</p>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
  `
})
export class TimeTrackingComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly timeApi = inject(TimeTrackingService);
  private readonly reports = inject(PmReportsApiService);
  private tickHandle: ReturnType<typeof setInterval> | null = null;

  readonly loading = signal(false);
  readonly loadingTimesheet = signal(false);
  readonly loadingUtil = signal(false);
  readonly error = signal<string | null>(null);
  readonly rows = signal<TimeEntryDto[]>([]);
  readonly projects = signal<ProjectOption[]>([]);
  readonly projectIssues = signal<IssueOption[]>([]);
  readonly timesheetDays = signal<TimesheetDayDto[]>([]);
  readonly timesheetTotal = signal(0);
  readonly timesheetBillable = signal(0);
  readonly timesheetNonBillable = signal(0);
  readonly utilization = signal<ResourceUtilizationReportDto | null>(null);
  readonly now = signal(Date.now());

  timerProjectId = '';
  timerIssueId = '';
  logIssueId = '';
  timerDescription = '';
  manualProjectId = '';
  manualDate = new Date().toISOString().slice(0, 10);
  manualHours: number | null = null;
  manualDescription = '';
  manualBillable = true;
  timesheetPreset: TimesheetPreset = 'week';
  private employeeId: string | null = null;

  readonly timerColumns = ['description', 'hours', 'actions'];
  readonly logColumns = ['date', 'description', 'hours', 'status', 'actions'];
  readonly sheetColumns = ['description', 'hours', 'status'];
  readonly utilColumns = ['employeeName', 'openIssues', 'remainingStoryPoints', 'availableHours', 'isOverallocated'];

  ngOnInit(): void {
    this.loadProjects();
    this.load();
    this.loadTimesheet();
    this.tickHandle = setInterval(() => this.now.set(Date.now()), 1000);
  }

  ngOnDestroy(): void {
    if (this.tickHandle) clearInterval(this.tickHandle);
  }

  runningEntries(): TimeEntryDto[] {
    return this.rows().filter(r => r.isRunning);
  }

  loadProjects(): void {
    this.api.getPaged<ProjectOption>('v1/projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        const items = page.items ?? [];
        this.projects.set(items);
        if (items.length) {
          this.timerProjectId = items[0].id;
          this.manualProjectId = items[0].id;
          this.loadProjectIssues(items[0].id);
          this.loadUtilization(items[0].id);
        }
      },
      error: () => this.projects.set([])
    });
  }

  onManualProjectChange(projectId: string): void {
    this.loadProjectIssues(projectId);
    this.loadUtilization(projectId);
  }

  loadProjectIssues(projectId: string): void {
    if (!projectId) {
      this.projectIssues.set([]);
      return;
    }
    this.api.getPaged<IssueOption>('v1/issues', { page: 1, pageSize: 100, projectId } as never).subscribe({
      next: (page) => this.projectIssues.set((page.items ?? []).map(i => ({
        id: i.id, key: (i as any).key, summary: (i as any).summary
      }))),
      error: () => this.projectIssues.set([])
    });
  }

  loadUtilization(projectId: string): void {
    if (!projectId) {
      this.utilization.set(null);
      return;
    }
    this.loadingUtil.set(true);
    this.reports.resourceUtilization(projectId).subscribe({
      next: (r) => {
        this.utilization.set(r);
        this.loadingUtil.set(false);
      },
      error: () => {
        this.utilization.set(null);
        this.loadingUtil.set(false);
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.timeApi.list({ page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        const items = page.items ?? [];
        this.rows.set(items);
        if (!this.employeeId && items[0]?.employeeId) {
          this.employeeId = items[0].employeeId;
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load time entries.');
      }
    });
  }

  setTimesheetPreset(preset: TimesheetPreset): void {
    this.timesheetPreset = preset;
    this.loadTimesheet();
  }

  private timesheetRange(): { from: string; to: string } {
    const to = new Date();
    const from = new Date();
    if (this.timesheetPreset === 'day') {
      // same day
    } else if (this.timesheetPreset === 'month') {
      from.setDate(1);
    } else {
      const day = from.getDay();
      from.setDate(from.getDate() - day);
    }
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10)
    };
  }

  loadTimesheet(): void {
    this.loadingTimesheet.set(true);
    const { from, to } = this.timesheetRange();
    const params: { from: string; to: string; employeeId?: string } = { from, to };
    if (this.employeeId) params.employeeId = this.employeeId;
    this.timeApi.getTimesheet(params).subscribe({
      next: (sheet) => {
        if (sheet.employeeId) this.employeeId = sheet.employeeId;
        this.timesheetDays.set(sheet.days ?? []);
        this.timesheetTotal.set(sheet.totalHours ?? 0);
        this.timesheetBillable.set(sheet.billableHours ?? 0);
        this.timesheetNonBillable.set(sheet.nonBillableHours ?? Math.max(0, (sheet.totalHours ?? 0) - (sheet.billableHours ?? 0)));
        this.loadingTimesheet.set(false);
      },
      error: () => {
        this.groupTimesheetFromRows();
        this.loadingTimesheet.set(false);
      }
    });
  }

  private groupTimesheetFromRows(): void {
    const map = new Map<string, TimeEntryDto[]>();
    for (const row of this.rows()) {
      const list = map.get(row.date) ?? [];
      list.push(row);
      map.set(row.date, list);
    }
    const days: TimesheetDayDto[] = [...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, entries]) => ({
        date,
        entries,
        totalHours: entries.reduce((s, e) => s + Number(e.hours ?? 0), 0)
      }));
    this.timesheetDays.set(days);
    const total = days.reduce((s, d) => s + d.totalHours, 0);
    const billable = this.rows().filter(r => r.isBillable).reduce((s, e) => s + Number(e.hours ?? 0), 0);
    this.timesheetTotal.set(total);
    this.timesheetBillable.set(billable);
    this.timesheetNonBillable.set(Math.max(0, total - billable));
  }

  startTimer(): void {
    this.timeApi.startTimer({
      projectId: this.timerProjectId || undefined,
      issueId: this.timerIssueId || undefined,
      description: this.timerDescription.trim() || undefined
    }).subscribe({
      next: () => { this.timerDescription = ''; this.load(); },
      error: (err) => this.error.set(err?.message || 'Failed to start timer.')
    });
  }

  pauseTimer(row: TimeEntryDto): void {
    this.timeApi.pauseTimer(row.id).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to pause timer.')
    });
  }

  resumeTimer(row: TimeEntryDto): void {
    this.timeApi.resumeTimer(row.id).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to resume timer.')
    });
  }

  stopTimer(row: TimeEntryDto): void {
    this.timeApi.stopTimer(row.id).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to stop timer.')
    });
  }

  createManual(): void {
    if (!this.manualHours) return;
    this.timeApi.createManual({
      employeeId: this.employeeId || undefined,
      projectId: this.manualProjectId || undefined,
      issueId: this.logIssueId || undefined,
      date: this.manualDate,
      hours: this.manualHours,
      description: this.manualDescription.trim() || undefined,
      isBillable: this.manualBillable
    }).subscribe({
      next: () => {
        this.manualHours = null;
        this.manualDescription = '';
        this.load();
        this.loadTimesheet();
      },
      error: (err) => this.error.set(err?.message || 'Failed to log time.')
    });
  }

  submitEntry(row: TimeEntryDto): void {
    this.timeApi.submit(row.id).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to submit entry.')
    });
  }

  liveHours(row: TimeEntryDto): string {
    this.now();
    if (!row.startedAt) return '0.00';
    const hours = (Date.now() - new Date(row.startedAt).getTime()) / 3_600_000;
    return Math.max(hours, 0).toFixed(2);
  }

  statusLabel(status?: number): string {
    return TIME_ENTRY_STATUS_LABELS[status ?? 0] ?? String(status);
  }

  statusChipClass(row: TimeEntryDto): string {
    if (row.isRunning) return 'running';
    return ['draft', 'submitted', 'approved', 'rejected'][row.status ?? 0] ?? 'draft';
  }
}
